// Station Explorer - A web interface for selecting and accessing seismic station data from EIDA's federated data centers.

// Copyright (C) 2025 GFZ Helmholtz Centre for Geosciences, Heesun Joo heesun@gfz.de

// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published
// by the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.

// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.

// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see https://www.gnu.org/licenses/



import { createApp } from 'vue'
import { createPinia } from 'pinia'

// Import PrimeVue components
import PrimeVue from 'primevue/config'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import TabMenu from 'primevue/tabmenu'
import Dialog from 'primevue/dialog'
import ToastService from 'primevue/toastservice'
import Toast from 'primevue/toast'

// Import styles
import 'primevue/resources/themes/aura-light-green/theme.css'
import 'primevue/resources/primevue.min.css'
import 'primeicons/primeicons.css'
import '@/assets/css/main.css'

// Fetch runtime config into window.__APP_CONFIG__ before dynamically importing the app, so config.json is in place when the resolver evaluates.
async function boot() {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-cache' })
    if (res.ok) window.__APP_CONFIG__ = await res.json()
  } catch (_) { }

  const { config, applyTheme } = await import('@/config')
  applyTheme()
  if (config.brand.title) document.title = config.brand.title

  const { default: App } = await import('./App.vue')
  const { createWindowFDSNWSControl } = await import('@/services/fdsnwsClient.js')

  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)
  app.use(PrimeVue)
  app.use(ToastService)
  app.component('DataTable', DataTable)
  app.component('Column', Column)
  app.component('Button', Button)
  app.component('TabMenu', TabMenu)
  app.component('Dialog', Dialog)
  app.component('Toast', Toast)

  // Provide a single FDSNWS control instance app-wide
  let control = null
  try {
    control = createWindowFDSNWSControl({ routing: config.api.routingEnabled, routerURL: config.api.routingUrl, fdsnwsURL: config.api.fdsnBase })
    app.provide('wiFDSNWS_Control', control)
    control.init().then(() => {
      window.wiFDSNWS_Control = control
      try {
        const t = localStorage.getItem('se_access_token')
        if (t && control && control.setAuthToken) control.setAuthToken(t)
      } catch (_) {}
    })
  } catch (_e) {
  }

  app.mount('#app')
}

boot()
